import { useEffect, useMemo } from 'react';
import { Box, FormHelperText, styled, Typography } from '@mui/material';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import dateUtils from '@/services/DateUtils';

import type { ReservationFormInput } from '../schema/reservationForm.schema';
import {
  isAppointmentDateRequired,
  isAppointmentDateTimeRequired,
  isPickupDateTimeRequired,
} from '../schema/reservationForm.schema';
import { TripArrayFieldDateInput } from './TripArrayFieldDateInput';
import { ReasonForTripField } from './ReasonForTripField';
import { TripArrayFieldDateTimeInput } from './TripArrayFieldDateTimeInput';
import { WillCallSelector } from './WillCallSelector';
import { TripArrayFieldPhoneInput } from './TripArrayFieldPhoneInput';
import { PickupNotesField } from './PickupNotesField';
import { DropoffNotesField } from './DropoffNotesField';
import { PickupAddressField } from './PickupAddressField';
import { DropoffAddressField } from './DropoffAddressField';
import { DropoffAddressValidationMessage } from '../messages/DropoffAddressValidationMessage';
import { LevelOfCareSection } from './LevelOfCareSection';
import { RoundTripReturnTripFields } from './RoundTripReturnTripFields';
import { NeedsApprovalMessage } from '../messages/GurneyNeedsApprovalMessage';
import { CareLevelMismatchMessage } from '../messages/CareLevelMismatchMessage';
import { EstimatedTravelTimeMessage } from '../messages/EstimatedTravelTimeMessage';
import { useReservationFormMachines } from '../ReservationFormMachinesContext';
import { useFieldActorSnapshot } from '../useFieldActorSnapshot';
import type { TripLegEstimatedPickupContext } from '../machines/tripLegEstimatedPickup.machine';

const PREFIX = 'TripLegFieldsRow';

const classes = {
  container: `${PREFIX}-container`,
  fieldSection: `${PREFIX}-fieldSection`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  [`& .${classes.fieldSection}`]: {
    marginTop: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

function isAddressFilled(address: unknown): boolean {
  if (typeof address === 'string') return address.trim().length > 0;
  if (address && typeof address === 'object') {
    const a = address as { isHeader?: boolean; place_id?: string; mainText?: string; description?: string };
    if (a.isHeader || a.place_id === 'header') return false;
    return Boolean(a.place_id?.trim() || a.mainText?.trim() || a.description?.trim());
  }
  return false;
}

export function TripLegFieldsRow({
  index,
  tripLabel,
  showRoundTripReturnFields = false,
}: {
  index: number;
  tripLabel?: string;
  showRoundTripReturnFields?: boolean;
}) {
  const { t } = useTranslation('common');
  const { control, getFieldState, setValue } = useFormContext<ReservationFormInput>();
  const { errors } = useFormState({ control, name: 'genericErrors' });
  const genericErrorMessages =
    errors.genericErrors?.message != null
      ? String(errors.genericErrors.message)
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  const isWillCall =
    useWatch({
      control,
      name: `trips.${index}.isWillCall`,
    }) ?? false;
  
  const { fieldRefs } = useReservationFormMachines();
  const estimatedPickupSnapshot = useFieldActorSnapshot(
    fieldRefs.tripLegEstimatedPickup,
  );
  const estimatedCtx =
    estimatedPickupSnapshot.context as TripLegEstimatedPickupContext;
  const directionsTravel = estimatedCtx.directionsTravel;
  const estimatedPickupTime = estimatedCtx.estimatedPickupTime;

  const appointmentDateTime = useWatch({
    control,
    name: `trips.${index}.appointmentDateTime`,
  }) as DateTime | null | undefined;
  const pickupDateTimeValue = useWatch({
    control,
    name: `trips.${index}.pickupDateTime`,
  }) as DateTime | null | undefined;

  useEffect(() => {
    if (index !== 0 || isWillCall) return;
    if (!estimatedPickupTime) return;
    if (!appointmentDateTime?.isValid) return;

    const pickupDateTimePath = `trips.${index}.pickupDateTime` as const;
    const pickupDatePath = `trips.${index}.pickupDate` as const;

    if (getFieldState(pickupDateTimePath).isDirty || getFieldState(pickupDatePath).isDirty) {
      return;
    }

    const facilityTimeZone = dateUtils.WORK_TIME_ZONE;
    let basePickupDateTime = appointmentDateTime;
    if (dateUtils.shouldDefaultToPreviousDay(appointmentDateTime, facilityTimeZone)) {
      basePickupDateTime = appointmentDateTime.minus({ days: 1 });
    }
    const calculatedPickup = basePickupDateTime.minus({
      minutes: estimatedPickupTime.totalPickupTime,
    });

    if (
      pickupDateTimeValue instanceof DateTime &&
      pickupDateTimeValue.isValid &&
      pickupDateTimeValue.equals(calculatedPickup)
    ) {
      return;
    }

    setValue(pickupDateTimePath, calculatedPickup, {
      shouldValidate: true,
      shouldDirty: false,
    });
    setValue(pickupDatePath, calculatedPickup.toISODate() ?? '', {
      shouldValidate: true,
      shouldDirty: false,
    });
    fieldRefs.pickupDateTime.send({
      type: 'CHANGE',
      index,
      value: calculatedPickup.toISO() ?? '',
    });
  }, [
    index,
    isWillCall,
    estimatedPickupTime,
    appointmentDateTime,
    pickupDateTimeValue,
    getFieldState,
    setValue,
    fieldRefs.pickupDateTime,
  ]);

  const { travelTime, travelDistance } = useMemo(() => {
    if (index !== 0 || directionsTravel == null) {
      return { travelTime: '', travelDistance: '' };
    }
    return {
      travelTime: directionsTravel.durationText,
      travelDistance: directionsTravel.distanceText,
    };
  }, [directionsTravel, index]);

  const dateTimeReadOnly = !travelTime || !travelDistance;

  return (
    <StyledBox className={classes.container}>
      {tripLabel ? (
        <Typography variant="subtitle1" fontWeight={600}>
          {tripLabel}
        </Typography>
      ) : null}

      {index === 0
        ? genericErrorMessages.map((msg, idx) => (
            <FormHelperText error key={idx}>
              {msg}
            </FormHelperText>
          ))
        : null}

      <ReasonForTripField index={index} />

      {isWillCall ? (
        <TripArrayFieldDateInput
          tripField="appointmentDate"
          index={index}
          required={isAppointmentDateRequired(isWillCall)}
        />
      ) : (
        <TripArrayFieldDateTimeInput
          tripField="appointmentDateTime"
          index={index}
          required={isAppointmentDateTimeRequired(isWillCall)}
        />
      )}

      <Box className={classes.fieldSection}>
        <WillCallSelector index={index} />

        {!isWillCall && (
          <TripArrayFieldDateTimeInput
            tripField="pickupDateTime"
            index={index}
            required={isPickupDateTimeRequired(isWillCall)}
            readOnly={dateTimeReadOnly}
          />
        )}
        {travelTime && travelDistance ? (
          <EstimatedTravelTimeMessage time={travelTime} distance={travelDistance} />
        ) : null}

        <PickupAddressField index={index} />
        <PickupNotesField index={index} />
        <TripArrayFieldPhoneInput
          index={index}
          tripField="pickupPhone"
          extensionField="pickupPhoneExtension"
          label={t('member.phone')}
          required
        />
      </Box>

      <Box className={classes.fieldSection}>
        <Typography variant="h5" component="h3">
          {t('member.dropOff')}
        </Typography>

        <DropoffAddressField index={index} />
        <DropoffAddressValidationMessage index={index} />
        <DropoffNotesField index={index} />
        <TripArrayFieldPhoneInput
          index={index}
          tripField="dropoffPhone"
          extensionField="dropoffPhoneExtension"
          label={t('member.phone')}
        />
      </Box>

      {showRoundTripReturnFields ? (
        <RoundTripReturnTripFields index={index} />
      ) : null}

      <Box className={classes.fieldSection}>
        <LevelOfCareSection index={index} />
        <NeedsApprovalMessage index={index} />
        <CareLevelMismatchMessage index={index} />
      </Box>
    </StyledBox>
  );
}
